import {AREA_ZONES, SOUND_FILE_FIELDS} from "../spots/graphQl";
import gql from "graphql-tag";

const AREA_CONTENT_FIELDS = `
	fragment AreaContentFields on AreaContent {
		id,
		areaId
        organizationId
		name
        public
        publicAccessCode
        summary
        description
        coverImageUrl
        creator
        position {
            lat
            lng
        }
        altSyncServerAddress
        status
        hasUnpublishedChanges
        modifiedAt
        key
        showMap
        developerMode
        showSoundInfo
        showTransport
        defaultVolume
        setDefaultVolumeOnReset
        appOverridesSystemVolume
        disableVolumeButtons
        bleScanMode
        spotTriggerMode
        autoPlay
        discoverMode
        groupMode
        groupModeAutoWindow
        defaults {
            beaconTriggers {
                id
                type
                macAddr
                majorId
                minorId
                uuid
                note
            }
            wiFiTriggers {
                id
                type
                name
            }
            sharedTrigger {
                type
                beacon {
                    macAddr
                    uuid
                }
                wifi {
                    name
                }
            }
        }
        useBoundariesZone
        iosCompatible
        audioMode
        flowMode
        downloadFrom
        downloadTo
        playFrom
        playTo
        languages {
            default
            downloadMode
            available {
                code
                label
                flagUrl
            }
        }
        zones {
            boundaries {
                ...ZoneFields
            }
            maintenance {
                ...ZoneFields
            }
            entry {
                ...ZoneFields
            }
            exit {
                ...ZoneFields
            }
            ambientZones {
                ...ZoneFields
            }
        }
	}
`;

const AREA_FIELDS = `
	fragment AreaFields on Area {
		id,
        releasedName,
        versionId,
        status,
        hasUnpublishedChanges,
        organization {
            id
            name
        },
        modifiedAt,
        iosCompatible,
        coverImageUrl
	}
`;

export const getAreasQuery = gql(`
${AREA_FIELDS}
query GetAreas($organizationIds: [String]!, $includeArchived: Boolean) {
	getAreas(organizationIds: $organizationIds, includeArchived: $includeArchived) {
	    ...AreaFields
	}
  }
`);

export const getPublicAreasQuery = gql(`
query GetPublicAreas {
    getPublicAreas {
        id
        releasedName
        organizationId
        organizationName
        summary
        description
        creator
        coverImageUrl
        position {
            lat
            lng
        }
        availableLanguageCodes
        publishedAt
        hasAccessCode
        downloadFrom
        downloadTo
        playFrom
        playTo
    }
}
`);

export const getAreaStatisticsQuery = gql(`
query GetAreaStatistics($areaIds: [String]!) {
    getAreaStatistics(areaIds: $areaIds) {
        id
        areaId
        updatedAt
        createdAt
        audioStorage {
            draft {
                totalFilesCount
                totalFilesStorageSize
            }
            published {
                totalFilesCount
                totalFilesStorageSize
            }
            total {
                totalFilesCount
                totalFilesStorageSize
            }
        }
    }
}
`);

export const archiveAreaMutation = gql(`
${AREA_FIELDS}
    mutation ArchiveArea($id: String!, $status: String!) {
        archiveArea(id: $id, status: $status) {
            ...AreaFields
        }
    }
`);

export const purgeAreaMutation = gql(`
    mutation PurgeArea($id: String!) {
        purgeArea(id: $id) {
            message
        }
    }
`);

export const getOrganizationsQuery = gql(`
query GetOrganizations($organizationIds: [String]!) {
    getOrganizations(organizationIds: $organizationIds) {
        id
        name
        appVersion
        features
        rolePlan {
            id
            name
            maxAdministrators
            maxEditors
            isDefault
        }
        roleUsage {
            administrators
            editors
        }
        library {
            iBeaconUuids
        }
    }
}
`);

export const addOrganizationLibraryIBeaconUuidMutation = gql(`
    mutation AddOrganizationLibraryIBeaconUuid($organizationId: String!, $uuid: String!) {
        addOrganizationLibraryIBeaconUuid(organizationId: $organizationId, uuid: $uuid) {
            id
            library {
                iBeaconUuids
            }
        }
    }
`);

export const getOrganizationPlansQuery = gql(`
query GetOrganizationPlans {
    getOrganizationPlans {
        id
        name
        maxAdministrators
        maxEditors
        isDefault
        assignedOrganizationsCount
    }
}
`);

export const createOrganizationMutation = gql(`
    mutation CreateOrganization($name: String!, $appVersion: String) {
        createOrganization(name: $name, appVersion: $appVersion) {
            id
            name
            appVersion
        }
    }
`);

export const updateOrganizationMutation = gql(`
    mutation UpdateOrganization($id: String!, $name: String!, $appVersion: String) {
        updateOrganization(id: $id, name: $name, appVersion: $appVersion) {
            id
            name
            appVersion
        }
    }
`);

export const deleteOrganizationMutation = gql(`
    mutation DeleteOrganization($id: String!) {
        deleteOrganization(id: $id) {
            message
        }
    }
`);

export const setOrganizationFeatureMutation = gql(`
    mutation SetOrganizationFeature($organizationId: String!, $featureId: String!, $enabled: Boolean!) {
        setOrganizationFeature(organizationId: $organizationId, featureId: $featureId, enabled: $enabled) {
            id
            name
            appVersion
            features
            rolePlan {
                id
                name
                maxAdministrators
                maxEditors
                isDefault
            }
            roleUsage {
                administrators
                editors
            }
        }
    }
`);

export const setOrganizationPlanMutation = gql(`
    mutation SetOrganizationPlan($organizationId: String!, $organizationPlanId: String!) {
        setOrganizationPlan(organizationId: $organizationId, organizationPlanId: $organizationPlanId) {
            id
            name
            appVersion
            features
            rolePlan {
                id
                name
                maxAdministrators
                maxEditors
                isDefault
            }
            roleUsage {
                administrators
                editors
            }
        }
    }
`);

export const createOrganizationPlanMutation = gql(`
    mutation CreateOrganizationPlan($name: String!, $maxAdministrators: Int!, $maxEditors: Int!) {
        createOrganizationPlan(name: $name, maxAdministrators: $maxAdministrators, maxEditors: $maxEditors) {
            id
            name
            maxAdministrators
            maxEditors
            isDefault
        }
    }
`);

export const updateOrganizationPlanMutation = gql(`
    mutation UpdateOrganizationPlan($id: String!, $name: String!, $maxAdministrators: Int!, $maxEditors: Int!) {
        updateOrganizationPlan(id: $id, name: $name, maxAdministrators: $maxAdministrators, maxEditors: $maxEditors) {
            id
            name
            maxAdministrators
            maxEditors
            isDefault
            assignedOrganizationsCount
        }
    }
`);

export const deleteOrganizationPlanMutation = gql(`
    mutation DeleteOrganizationPlan($id: String!) {
        deleteOrganizationPlan(id: $id) {
            message
        }
    }
`);

export const createOrganizationInvitationMutation = gql(`
    mutation CreateOrganizationInvitation($organizationId: String!, $email: String!) {
        createOrganizationInvitation(organizationId: $organizationId, email: $email) {
            token
            expiresAt
            emailSent
        }
    }
`);


export const getAreaContentQuery = gql(`
${SOUND_FILE_FIELDS},
${AREA_ZONES},
${AREA_CONTENT_FIELDS},
query GetAreaContent($areaId: String!, $publishedAt: Float!) {
	getAreaContent(areaId: $areaId, publishedAt: $publishedAt) {
		...AreaContentFields
	}
  }
`);


export const updateAreaContentMutation = gql(`
${SOUND_FILE_FIELDS},
${AREA_ZONES},
${AREA_CONTENT_FIELDS},
    mutation UpdateAreaContent($areaId: String!, $name: String!, $public: Boolean, $publicAccessCode: String, $summary: String, $description: String, $coverImageUrl: String, $creator: String, $altSyncServerAddress: String, $playMode: String, $runOnes: Boolean,
                                $loop: Boolean, $playFullAfter: Int, $zones: AreaZonesIn,
                                $languages: AreaLanguagesIn, $showMap: Boolean, $developerMode: Boolean, $showSoundInfo: String, $showTransport: Boolean, $defaultVolume: Int, $setDefaultVolumeOnReset: Boolean, $appOverridesSystemVolume: Boolean, $disableVolumeButtons: Boolean, $bleScanMode: String, $spotTriggerMode: String, $autoPlay: Boolean, $discoverMode: String, $groupMode: [String], $groupModeAutoWindow: Int,
                                $defaults: AreaDefaultsIn,
                                $useBoundariesZone: Boolean,
                                $iosCompatible: Boolean,
                                $audioMode: String,
                                $flowMode: String,
                                $downloadFrom: String, $downloadTo: String, $playFrom: String, $playTo: String,
                                $position: LatLng,
                                $beaconTriggers: [BeaconTriggerIn], $wiFiTriggers: [WiFiAPTriggerIn],
                                $key: String!, $modifiedAt: Float!, $expectedModifiedAt: Float
                                ) {
        updateAreaContent(areaId: $areaId, name: $name, public: $public, publicAccessCode: $publicAccessCode, summary: $summary, description: $description, coverImageUrl: $coverImageUrl, creator: $creator, altSyncServerAddress: $altSyncServerAddress, playMode: $playMode, runOnes: $runOnes, loop: $loop, playFullAfter: $playFullAfter,
                            zones: $zones, languages: $languages, showMap: $showMap, developerMode: $developerMode, showSoundInfo: $showSoundInfo, showTransport: $showTransport, defaultVolume: $defaultVolume, setDefaultVolumeOnReset: $setDefaultVolumeOnReset, appOverridesSystemVolume: $appOverridesSystemVolume, disableVolumeButtons: $disableVolumeButtons, bleScanMode: $bleScanMode, spotTriggerMode: $spotTriggerMode, autoPlay: $autoPlay, discoverMode: $discoverMode, groupMode: $groupMode, groupModeAutoWindow: $groupModeAutoWindow, defaults: $defaults, useBoundariesZone: $useBoundariesZone, iosCompatible: $iosCompatible, audioMode: $audioMode, flowMode: $flowMode,
                            downloadFrom: $downloadFrom, downloadTo: $downloadTo, playFrom: $playFrom, playTo: $playTo,
                            position: $position,
                            beaconTriggers: $beaconTriggers, wiFiTriggers: $wiFiTriggers,
                            key: $key, modifiedAt: $modifiedAt, expectedModifiedAt: $expectedModifiedAt
                            ) {
            ...AreaContentFields
        }
    }
`);

export const createAreaMutation = gql(`
${AREA_FIELDS}
    mutation CreateArea($organizationId: String!, $name: String!) {
        createArea(organizationId: $organizationId, name: $name) {
            ...AreaFields
        }
    }
`);

export const acceptInvitationMutation = gql(`
    mutation AcceptInvitation($token: String!) {
        acceptInvitation(token: $token) {
            message
        }
    }
`);

export const createInvitationMutation = gql(`
    mutation CreateInvitation($areaId: String!, $email: String) {
        createInvitation(areaId: $areaId, email: $email) {
            token
            expiresAt
            emailSent
        }
    }
`);

export const refreshAreaStatisticsMutation = gql(`
    mutation RefreshAreaStatistics($areaId: String!) {
        refreshAreaStatistics(areaId: $areaId) {
            id
            areaId
            updatedAt
            createdAt
            audioStorage {
                draft {
                    totalFilesCount
                    totalFilesStorageSize
                }
                published {
                    totalFilesCount
                    totalFilesStorageSize
                }
                total {
                    totalFilesCount
                    totalFilesStorageSize
                }
            }
        }
    }
`);

// Backward-compatible exports while callers are migrated.
export const acceptAreaInvitationMutation = acceptInvitationMutation;
export const createAreaInvitationMutation = createInvitationMutation;
